"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";

interface CarouselSlide {
  id: string;
  title: string;
  description: string;
  icon: string;
  gradient: string;
}

export const DashboardCarousel = () => {
  const { t } = useTranslation(["workspaces", "common"]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides: CarouselSlide[] = [
    {
      id: "welcome",
      title: t("workspaces:dashboard.carousel.welcome.title"),
      description: t("workspaces:dashboard.carousel.welcome.description"),
      icon: "mdi:rocket-launch",
      gradient: "from-blue-400 to-purple-600",
    },
    {
      id: "collaborate",
      title: t("workspaces:dashboard.carousel.collaborate.title"),
      description: t("workspaces:dashboard.carousel.collaborate.description"),
      icon: "mdi:account-group",
      gradient: "from-green-400 to-blue-500",
    },
    {
      id: "learn",
      title: t("workspaces:dashboard.carousel.learn.title"),
      description: t("workspaces:dashboard.carousel.learn.description"),
      icon: "mdi:book-open-page-variant",
      gradient: "from-orange-400 to-red-500",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-foreground/50 relative mb-6 overflow-hidden rounded-2xl border-none p-4 backdrop-blur-xl sm:mb-8 sm:p-6"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          animate={{ opacity: 1, x: 0 }}
          className="flex flex-col items-center justify-between gap-4 md:flex-row"
          exit={{ opacity: 0, x: -20 }}
          initial={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex w-full flex-col items-center justify-center gap-4 sm:gap-6 md:flex-row md:items-start">
            <div
              className={`bg-gradient-to-br ${slides[currentSlide].gradient} my-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl sm:h-18 sm:w-18 sm:rounded-2xl`}
            >
              <Icon
                className="text-white"
                height="1.5rem"
                icon={slides[currentSlide].icon}
                width="1.5rem"
              />
            </div>
            <div className="my-auto flex h-auto w-full flex-col justify-center text-center md:h-24 md:text-left">
              <h3 className="text-accent-foreground text-lg font-bold md:text-xl">
                {slides[currentSlide].title}
              </h3>
              <p className="text-muted-foreground mt-0.5 w-full text-sm md:mt-1 md:text-base">
                {slides[currentSlide].description}
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-3 sm:gap-4">
            <div className="flex gap-1.5 sm:gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`h-1.5 rounded-full transition-all duration-300 sm:h-2 ${
                    index === currentSlide
                      ? "bg-accent-foreground w-5 sm:w-6"
                      : "bg-muted-foreground/30 w-1.5 sm:w-2"
                  }`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
            <Button
              className="bg-muted/20 hover:bg-muted/30 border-border/50 hidden sm:flex"
              size="sm"
              variant="outline"
            >
              <Icon className="mr-2" height="1rem" icon="mdi:arrow-right" width="1rem" />
              {t("common:learnMore")}
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};
